import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import LogoIcon from 'components/App/Header/Logo/LogoIcon'
import Clickable from 'components/Common/Clickable'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import config from 'constants/config'
import GeoContext from 'contexts/geoContext'
import ModalContext from 'contexts/ModalContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppSelector } from 'hooks/reduxHooks'
import useLocalizedNavItems from 'hooks/useLocalizedNavItems'
import useToggle from 'hooks/useToggle'
import TextButton from 'LuxKit/Button/TextButton'
import Flag from 'LuxKit/Flag'
import LineAngleDownBIcon from 'LuxKit/Icons/line/LineAngleDownBIcon'
import LineAngleRightBIcon from 'LuxKit/Icons/line/LineAngleRightBIcon'
import LineAngleUpBIcon from 'LuxKit/Icons/line/LineAngleUpBIcon'
import LineUserCircleIcon from 'LuxKit/Icons/line/LineUserCircleIcon'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { EmptyArray } from 'lib/array/arrayUtils'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { rem } from 'polished'
import type { MouseEventHandler } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { selectLoggedIn } from 'selectors/accountSelectors'
import {
  getFlatNavItems,
  MALDIVES_BUNDLES_NAV_ID,
} from 'selectors/navigationSelectors'
import styled from 'styled-components'
import { useCanAccessMobileChat } from 'tripPlanner/hooks/useShouldUseChat'
import { CHAT_TRIP_PLANNER_ROUTE } from 'tripPlanner/pages/chat/ChatTripPlannerPage/hooks/useChatNavigation'
import NavBarCurrencyLanguageModal from '../NavBarButtons/NavBarCurrencyLanguageModal'
import { useVisibleLanguageOptions } from '../NavBarButtons/useVisibleLanguageOptions'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import NavBarHamburgerMenuContactsAccordion from './NavBarHamburgerMenuContactsAccordion'
import NavBarHamburgerMenuLoyaltyFooter from './NavBarHamburgerMenuLoyaltyFooter'
import NavBarHamburgerMenuLoyaltyItem from './NavBarHamburgerMenuLoyaltyItem'
import NavBarHamburgerMenuMaldivesBundlesBanner from './NavBarHamburgerMenuMaldivesBundlesBanner'
import NavBarHamburgerMenuToggleItem from './NavBarHamburgerMenuToggleItem'
import NavBarHamburgerMenuVisitUsBanner from './NavBarHamburgerMenuVisitUsBanner'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import BodyText from 'LuxKit/Typography/BodyText'
import { AI_TRIP_PLANNER_NAV_ID } from './constants'

const NavItemGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${rem(8)};
`

const MoreToggle = styled(Clickable)`
  width: 100%;
  padding: ${rem(8)} ${rem(20)};
  border: 1px solid
    ${(props) => props.theme.palette.neutral.surface.lightForeground};
  text-align: center;
  border-radius: ${(props) => props.theme.borderRadius.M};
  transition: 200ms background-color, 200ms border-color;
  outline: 2px solid transparent;
  outline-offset: 2px;

  ${mediaHoverable} {
    &:hover {
      background-color: ${(props) =>
        props.theme.palette.neutral.surface.lightBackground};
    }
  }

  &:focus-visible {
    outline-color: ${(props) =>
      props.theme.palette.neutral.surface.lightForeground};
  }
`

const CurrencyLanguageListItem = styled(ClickableListItem)`
  padding: ${rem(8)};
`

/** Max toggle tiles in the two-column grid (6 rows x 2). */
const GRID_ICON_SLOT_COUNT = 12
const STORE_VISIT_NAV_ID = 'nav-link-store'
const HAMBURGER_MENU_NAV_ORDER = [
  'nav-link-hotels',
  'nav-link-homes-and-villas',
  'nav-link-flights',
  'nav-link-tours',
  'nav-link-cruises',
  'nav-link-experiences',
  'nav-link-car-hire',
  'nav-link-marketplace',
  AI_TRIP_PLANNER_NAV_ID,
  'nav-link-ultralux',
  'nav-link-dream-magazine',
  'nav-link-support',
  'nav-link-le-live',
  'nav-link-gift-cards',
  'nav-link-insurance',
  'nav-link-golf',
  'nav-link-refer',
  'nav-link-groups',
  'nav-link-weddings',
  'nav-link-le4b',
]
const HAMBURGER_MENU_NAV_ORDER_BY_ID = new Map(
  HAMBURGER_MENU_NAV_ORDER.map((navId, index) => [navId, index]),
)

export function orderNavItemsForHamburgerMenu(
  navItemsForMenu: Array<App.NavItem>,
  additionalNavItems: Array<App.NavItem>,
): Array<App.NavItem> {
  return [...navItemsForMenu, ...additionalNavItems]
    .map((navItem, index) => ({ navItem, index }))
    .sort((a, b) => {
      const aOrder =
        HAMBURGER_MENU_NAV_ORDER_BY_ID.get(a.navItem.id ?? '') ??
        Number.MAX_SAFE_INTEGER
      const bOrder =
        HAMBURGER_MENU_NAV_ORDER_BY_ID.get(b.navItem.id ?? '') ??
        Number.MAX_SAFE_INTEGER

      if (aOrder !== bOrder) return aOrder - bOrder

      return a.index - b.index
    })
    .map(({ navItem }) => navItem)
}

export function buildAdditionalHamburgerNavItems({
  aiTripPlannerText,
  tripChatMobileEnabled,
  tripPlannerEnabled,
}: {
  aiTripPlannerText: string
  tripChatMobileEnabled: boolean
  tripPlannerEnabled: boolean
}): Array<App.NavItem> {
  const items: Array<App.NavItem> = []

  if (tripPlannerEnabled && tripChatMobileEnabled) {
    items.push({
      id: AI_TRIP_PLANNER_NAV_ID,
      text: aiTripPlannerText,
      to: CHAT_TRIP_PLANNER_ROUTE,
      sideNavIconName: 'sparkles',
      category: 'tertiary',
    })
  }

  return items.length ? items : EmptyArray
}

function NavBarHamburgerMenu() {
  const intl = useIntl()
  const [showMore, toggleMore] = useToggle()
  const { currentRegionCode, currentCurrency } = useContext(GeoContext)
  const regionCode = useAppSelector((state) => state.geo.currentRegionCode)
  const { resolve } = useModalElementContext()
  const showModal = useContext(ModalContext)
  const loggedIn = useAppSelector(selectLoggedIn)
  const canAccessMobileChat = useCanAccessMobileChat()
  const rawNavItems = useAppSelector((state) => getFlatNavItems(state))
  const navItems = useLocalizedNavItems(rawNavItems)
  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const loyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const isCurrentRegionUserLoyaltyRegion = useAppSelector(
    getIsCurrentRegionUserLoyaltyRegion,
  )
  const { showLanguageSelector } = useVisibleLanguageOptions()

  const onMoreClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      toggleMore()
      if (!showMore) {
        e.currentTarget.scrollIntoView({ block: 'start', behavior: 'smooth' })
      }
    },
    [showMore, toggleMore],
  )

  const onCurrencyLanguageClick = useCallback(() => {
    showModal(
      <NavBarCurrencyLanguageModal
        showLanguageTab={showLanguageSelector}
        tabletHeight="max"
      />,
    )
  }, [showModal, showLanguageSelector])
  const onLogin = useCallback(
    () => showModal(<AccountAccessModal />),
    [showModal],
  )

  const closeModal = useCallback(() => resolve(), [resolve])
  const onNavItemClick = useCallback(
    (_navItem: App.NavItem) => {
      closeModal()
    },
    [closeModal],
  )

  const maldivesBundlesItem = useMemo(
    () => navItems.find((nav) => nav.id === MALDIVES_BUNDLES_NAV_ID),
    [navItems],
  )
  const storeVisitItem = useMemo(
    () => navItems.find((nav) => nav.id === STORE_VISIT_NAV_ID),
    [navItems],
  )
  const aiTripPlannerText = intl.formatMessage({
    id: 'pM0wTQYqeOugBRwAVxVI',
    defaultMessage: 'AI TripPlanner',
    description: 'AI TripPlanner navigation button text in the hamburger menu',
  })

  const navItemsForMenu = useMemo(
    () =>
      navItems.filter((nav) => {
        if (nav.id === MALDIVES_BUNDLES_NAV_ID) {
          return false
        }

        if (nav.id === STORE_VISIT_NAV_ID) {
          return false
        }

        return true
      }),
    [navItems],
  )

  const additionalNavItems = useMemo<Array<App.NavItem>>(() => {
    return buildAdditionalHamburgerNavItems({
      aiTripPlannerText,
      tripChatMobileEnabled: canAccessMobileChat,
      tripPlannerEnabled: config.ENABLE_TRIP_PLANNER,
    })
  }, [aiTripPlannerText, canAccessMobileChat])

  const [gridNavItems, moreItems] = useMemo(() => {
    const ordered = orderNavItemsForHamburgerMenu(
      navItemsForMenu,
      additionalNavItems,
    )
    const capped = Math.min(GRID_ICON_SLOT_COUNT, ordered.length)
    const evenCount = capped - (capped % 2)
    const grid = ordered.slice(0, evenCount)
    const more = ordered.slice(evenCount)
    return [grid, more]
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- `regionCode` forces recomputation on region change; nav array ref can lag
  }, [additionalNavItems, navItemsForMenu, regionCode])
  const visibleGridNavItems = showMore
    ? [...gridNavItems, ...moreItems]
    : gridNavItems

  const lessLabel = intl.formatMessage({
    id: 'ljFG-cTrLfYbBIgYMpaN',
    defaultMessage: 'Less',
    description:
      'Label on the expandable control in the hamburger menu that hides additional navigation destinations.',
  })
  const moreLabel = intl.formatMessage({
    id: 'gkS-ZiqSxK8HaWzfalf0',
    defaultMessage: 'More',
    description:
      'Label on the expandable control in the hamburger menu that reveals additional navigation destinations.',
  })
  const seeMoreLabel = showMore ? lessLabel : moreLabel

  return (
    <ModalBase mode="menu">
      <ModalHeader
        title={
          <Clickable onClick={closeModal} to={`/${config.defaultHomePage}`}>
            <LogoIcon height="1em" width={null} />
          </Clickable>
        }
      />
      <ModalBody>
        <ModalContent>
          <Group direction="vertical" gap={20}>
            {maldivesBundlesItem && (
              <NavBarHamburgerMenuMaldivesBundlesBanner
                navItem={maldivesBundlesItem}
                onNavigate={() => onNavItemClick(maldivesBundlesItem)}
              />
            )}
            <Group direction="vertical" gap={8}>
              {!!visibleGridNavItems.length && (
                <NavItemGrid>
                  {visibleGridNavItems.map((item) => (
                    <NavBarHamburgerMenuToggleItem
                      key={item.id ?? item.text}
                      navItem={item}
                      onClick={() => onNavItemClick(item)}
                    />
                  ))}
                </NavItemGrid>
              )}
              {!!moreItems.length && (
                <MoreToggle onClick={onMoreClick} aria-expanded={showMore}>
                  <BodyText variant="medium">
                    {seeMoreLabel}
                    {showMore && <LineAngleUpBIcon />}
                    {!showMore && <LineAngleDownBIcon />}
                  </BodyText>
                </MoreToggle>
              )}
            </Group>
            <Group direction="vertical" gap={8} noDisplayWhenEmpty>
              {luxPlusEnabled && (
                <NavBarHamburgerMenuLoyaltyItem
                  variant="luxPlus"
                  onClick={closeModal}
                />
              )}
              {loyaltyEnabled && (
                <NavBarHamburgerMenuLoyaltyItem
                  variant="loyalty"
                  onClick={closeModal}
                  isNew
                />
              )}
              {storeVisitItem && (
                <NavBarHamburgerMenuVisitUsBanner
                  navItem={storeVisitItem}
                  onNavigate={() => onNavItemClick(storeVisitItem)}
                />
              )}
            </Group>
            <NavBarHamburgerMenuContactsAccordion onItemClick={closeModal} />
            <CurrencyLanguageListItem
              size="medium"
              title={currentCurrency}
              onClick={onCurrencyLanguageClick}
              startIcon={<Flag countryCode={currentRegionCode} size="S" />}
              endIcon={<CSSBreakpoint max="mobile" as={LineAngleRightBIcon} />}
            />
          </Group>
        </ModalContent>
      </ModalBody>
      {!isCurrentRegionUserLoyaltyRegion && hasLoyaltyAccount && (
        <NavBarHamburgerMenuLoyaltyFooter />
      )}
      {!loggedIn && (
        <ModalFooter>
          <TextButton
            kind="secondary"
            onClick={onLogin}
            size="large"
            fit="full-width"
            startIcon={<LineUserCircleIcon />}
          >
            {intl.formatMessage({
              id: 'DVut0himStXU8yyJTSJu',
              defaultMessage: 'Sign up or Login',
              description:
                'Action button label for sign up or login action in the navigation bar',
            })}
          </TextButton>
        </ModalFooter>
      )}
    </ModalBase>
  )
}

export default NavBarHamburgerMenu
